/**
 * @fileoverview Shared Axios instance with authentication interceptors.
 *
 * This module creates a single Axios instance used by:
 * - useApi hook (hooks/useApi.ts)
 * - Page components via direct import
 *
 * Authentication flow:
 * 1. Request interceptor: adds Authorization: Bearer {accessToken} header
 * 2. Response interceptor: unwraps response.data, on 401 calls refresh()
 * 3. If refresh succeeds: retries the original request with the new token
 * 4. If refresh fails: rejects the promise (AuthProvider clears state, user is logged out)
 *
 * The access token is stored in memory (not localStorage) via a getter function
 * that AuthProvider registers. This keeps the security model intact — the token
 * never touches persistent storage.
 *
 * IMPORTANT: Because the response interceptor unwraps response.data, the exported
 * `api` object is a typed wrapper whose methods return Promise<T> directly (not
 * Promise<AxiosResponse<T>>). This keeps the compile-time types aligned with the
 * runtime shape. Do NOT access .data on the result:
 *   const data = await api.get<User[]>('/users');  // correct — data is User[]
 *   const resp = await api.get<User[]>('/users'); resp.data;  // WRONG — double unwrap
 *
 * @example
 * import { api } from '../core/api-client';
 * const users = await api.get<User[]>('/api/v1/users');
 *
 * // In AuthProvider (registers token access)
 * import { setAuthTokenGetter, setAuthRefreshFn } from '../core/api-client';
 * setAuthTokenGetter(() => tokenStorage.getAccessToken());
 * setAuthRefreshFn(async () => { ... });
 */
import axios, { AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import { API_BASE_URL } from './config';

// =============================================================================
// Token access — set by AuthProvider, read by interceptors
// =============================================================================

/**
 * Function that returns the current access token from AuthProvider state.
 * Registered by AuthProvider on mount.
 */
let getAccessToken: (() => string | null) | null = null;

/**
 * Function that attempts to refresh the access token.
 * Returns the new token on success, null on failure.
 * Registered by AuthProvider on mount.
 */
let refreshAccessToken: (() => Promise<string | null>) | null = null;

/**
 * Called by AuthProvider to register the token getter.
 * This avoids a circular dependency between api-client and AuthProvider.
 */
export function setAuthTokenGetter(getter: () => string | null): void {
  getAccessToken = getter;
}

/**
 * Called by AuthProvider to register the refresh function.
 */
export function setAuthRefreshFn(fn: () => Promise<string | null>): void {
  refreshAccessToken = fn;
}

// =============================================================================
// Axios instance (internal — do not export directly)
// =============================================================================

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30_000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Always send httpOnly cookies (refresh token)
});

// =============================================================================
// Request interceptor — attach Bearer token
// =============================================================================

axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getAccessToken?.();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// =============================================================================
// Response interceptor — unwrap data + handle 401 with token refresh
// =============================================================================

/** Flag to prevent multiple simultaneous refresh attempts */
let isRefreshing = false;

/**
 * Queue of requests waiting for the token refresh to complete.
 * When refresh succeeds, all queued requests are retried with the new token.
 * When refresh fails, all queued requests are rejected.
 */
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: Error) => void;
}> = [];

/**
 * Process the queue of failed requests after a refresh attempt.
 */
function processQueue(error: Error | null, token: string | null): void {
  for (const { resolve, reject } of failedQueue) {
    if (error || !token) {
      reject(error ?? new Error('Token refresh failed'));
    } else {
      resolve(token);
    }
  }
  failedQueue = [];
}

axiosInstance.interceptors.response.use(
  // Success — unwrap to response.data for consistent API
  (response) => response.data,

  // Error — handle 401
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // Only handle 401 (Unauthorized), and only once per request
    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    // Don't try to refresh if we don't have a refresh function
    if (!refreshAccessToken) {
      return Promise.reject(error);
    }

    // Don't try to refresh auth endpoints themselves (infinite loop prevention)
    const url = originalRequest.url || '';
    if (url.includes('/auth/login') || url.includes('/auth/refresh') || url.includes('/auth/register')) {
      return Promise.reject(error);
    }

    // If already refreshing, queue this request
    if (isRefreshing) {
      return new Promise<string>((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${token}`;
        }
        originalRequest._retry = true;
        return axiosInstance(originalRequest);
      });
    }

    // Start refresh
    isRefreshing = true;
    originalRequest._retry = true;

    try {
      const newToken = await refreshAccessToken();

      if (newToken) {
        // Refresh succeeded — retry original request and process queue
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
        }
        processQueue(null, newToken);
        return axiosInstance(originalRequest);
      } else {
        // Refresh returned null — session expired
        const refreshError = new Error('Session expired');
        processQueue(refreshError, null);
        return Promise.reject(refreshError);
      }
    } catch (refreshError) {
      // Refresh threw — network error or server error
      const err = refreshError instanceof Error ? refreshError : new Error('Token refresh failed');
      processQueue(err, null);
      return Promise.reject(err);
    } finally {
      isRefreshing = false;
    }
  }
);

// =============================================================================
// Typed wrapper — matches the response interceptor that unwraps `response.data`
//
// Every method returns Promise<T> (not AxiosResponse<T>) so that compile-time
// types match the runtime shape. Without this wrapper, callers would have to
// either cast every result or double-unwrap with `.data`.
// =============================================================================

export const api = {
  get: <T = unknown>(url: string, config?: AxiosRequestConfig) =>
    axiosInstance.get(url, config) as unknown as Promise<T>,

  post: <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    axiosInstance.post(url, data, config) as unknown as Promise<T>,

  put: <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    axiosInstance.put(url, data, config) as unknown as Promise<T>,

  patch: <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    axiosInstance.patch(url, data, config) as unknown as Promise<T>,

  delete: <T = unknown>(url: string, config?: AxiosRequestConfig) =>
    axiosInstance.delete(url, config) as unknown as Promise<T>,

  request: <T = unknown>(config: AxiosRequestConfig) =>
    axiosInstance.request(config) as unknown as Promise<T>,
};

export default api;